import * as settings from "../constants/settings";
import * as languages from "../constants/languages";
import * as componentTypes from "../constants/componentTypes";
import * as keyCodes from "../constants/keyCodes";
import * as rcc from "../constants/rcc";
import * as config from "../constants/config";
import * as settingsReducer from "../reducers/settingsReducer";
import * as getterHelper from "./getterHelper";
import * as validationHelper from "./validationHelper";

const isPricingNavigationItem = (item) => {
    switch (item) {
        case settings.NAV_ITEM_COLOR_SEARCH_TAB:
        case settings.NAV_ITEM_COLOR_TAB:
        case settings.NAV_ITEM_CUSTOM_FORMULA:
        case settings.NAV_ITEM_CUSTOMERS:
        case settings.NAV_ITEM_FAVORITES:
        case settings.NAV_ITEM_MY_ORDERS:
        case settings.NAV_ITEM_SEARCH_NEW_FORMULA:
        case settings.NAV_ITEM_SPECTRO:
        case settings.NAV_ITEM_BARCODE_SCAN:
            return true;
        default:
            return false;
    }
};

const getNavigationItemsByLicense = (items, productPricing) => {
    if (!productPricing) {
        return items;
    }

    let validItems = [];

    let item;
    for (item of items) {
        if (isPricingNavigationItem(item)) {
            validItems.push(item);
        }
    }

    return validItems;
};

const isPricingShortcut = (shortcut) => {
    switch (shortcut) {
        case settings.SHORTCUT_CHANGELOG:
            return true;
        case settings.SHORTCUT_DISPENSING:
            return false;
        case settings.SHORTCUT_NEW_TAB:
            return true;
        case settings.SHORTCUT_CLOSE_TAB:
            return true;
        case settings.SHORTCUT_QUICK_TINT:
            return false;
        case settings.SHORTCUT_SPECTRO:
            return true;
        case settings.SHORTCUT_CANISTERS:
            return false;
        case settings.SHORTCUT_MY_ORDERS:
            return true;
        case settings.SHORTCUT_PRINT:
            return true;
        case settings.SHORTCUT_ORDER:
            return true;
        case settings.SHORTCUT_TO_DISPENSE:
            return false;
        case settings.SHORTCUT_LOG_IN_AS:
            return true;
        case settings.SHORTCUT_HINT_TOOLTIPS:
            return true;
        default:
            return false;
    }
};

const getShortcutsByLicense = (shortcuts, productPricing) => {
    if (!productPricing) {
        return shortcuts;
    }

    let validShortcuts = [];

    let shortcut;
    for (shortcut of shortcuts) {
        if (isPricingShortcut(shortcut)) {
            validShortcuts.push(shortcut);
        }
    }

    return validShortcuts;
};

export const processSearchPanelOrderSetting = (
    searchPanelOrder,
    formulaName,
    formulaCode,
    blendFormulaAndCode,
    base,
    baseInProductPackage,
    product,
    gammaSoftware
) => {
    if (gammaSoftware) {
        return [
            componentTypes.SEARCH_PANEL_FORMULA,
            componentTypes.SEARCH_PANEL_FANDECK,
            componentTypes.SEARCH_PANEL_PRODUCT,
            componentTypes.SEARCH_PANEL_BIPP
        ];
    }

    if (searchPanelOrder.length < 6) {
        if (!searchPanelOrder.includes(componentTypes.SEARCH_PANEL_BASE)) {
            searchPanelOrder.push(componentTypes.SEARCH_PANEL_BASE);
        }

        if (!searchPanelOrder.includes(componentTypes.SEARCH_PANEL_BIPP)) {
            searchPanelOrder.push(componentTypes.SEARCH_PANEL_BIPP);
        }
    }

    let searchPanel = [];

    let validProduct,
        validFandeck,
        validColor = false;

    for (let item of searchPanelOrder) {
        switch (item) {
            case componentTypes.SEARCH_PANEL_PRODUCT:
                if (product) {
                    searchPanel.push(item);
                    validProduct = true;
                }
                break;
            case componentTypes.SEARCH_PANEL_FANDECK:
                searchPanel.push(item);
                validFandeck = true;
                break;
            case componentTypes.SEARCH_PANEL_FORMULA_NAME:
                if (blendFormulaAndCode) {
                    searchPanel.push(componentTypes.SEARCH_PANEL_FORMULA);
                    validColor = true;
                } else if (formulaName) {
                    searchPanel.push(item);
                    validColor = true;
                }
                break;
            case componentTypes.SEARCH_PANEL_FORMULA_CODE:
                if (formulaCode && !blendFormulaAndCode) {
                    searchPanel.push(item);
                    validColor = true;
                }
                break;
            case componentTypes.SEARCH_PANEL_BASE:
                if (base) {
                    searchPanel.push(item);
                }
                break;
            case componentTypes.SEARCH_PANEL_BIPP:
                if (baseInProductPackage) {
                    searchPanel.push(item);
                    validProduct = true;
                }
                break;
            default:
                break;
        }
    }

    if (validProduct && validFandeck && validColor) {
        return searchPanel;
    }

    return [
        componentTypes.SEARCH_PANEL_PRODUCT,
        componentTypes.SEARCH_PANEL_FANDECK,
        componentTypes.SEARCH_PANEL_FORMULA_NAME
    ];
};

export const getNavigationItemsSettingsByLicense = (items, productPricing) => {
    if (!Array.isArray(items)) {
        return [];
    }

    if (!productPricing) {
        return items;
    }

    let validItems = [];

    let item;
    for (item of items) {
        if (isPricingNavigationItem(item.id)) {
            validItems.push(item);
        }
    }

    return validItems;
};

const getKeyboardShortcuts = (shortCutsData) => {
    if (typeof shortCutsData === "object") {
        let shortCuts = {};

        for (let shortCut of Object.entries(shortCutsData)) {
            if (shortCut[0] !== undefined && typeof shortCut[1] === "object") {
                shortCuts[shortCut[0]] = shortCut[1];
            }
        }

        let difference = settings.SHORTCUTS_NAMES.filter((x) => !Object.keys(shortCuts).includes(x));

        for (let shortCutKey of difference) {
            shortCuts[shortCutKey] = {
                keyCode: keyCodes.Z,
                active: false,
                shift: false,
                ctrl: false
            };
        }

        return shortCuts;
    }

    return {};
};

export const parseUserSettings = (data, options, productPricing, redlikeLightSoftware, gammaSoftware, machineType) => {
    let settingState = Object.assign({}, settingsReducer.initialState);

    if (data[settings.AUTO_CLOSE_TAB] !== undefined) {
        if (typeof data[settings.AUTO_CLOSE_TAB] === "boolean") {
            settingState.autoCloseTab = data[settings.AUTO_CLOSE_TAB];
        }
    }

    if (data[settings.ADDITIONAL_TAB_TYPE] !== undefined) {
        if (typeof data[settings.ADDITIONAL_TAB_TYPE] === "string") {
            settingState.additionalTabType = data[settings.ADDITIONAL_TAB_TYPE];
        }
    }

    if (data[settings.BUILDING_SITE] !== undefined) {
        if (typeof data[settings.BUILDING_SITE] === "boolean") {
            settingState.buildingSite = data[settings.BUILDING_SITE];
        }
    }

    if (data[settings.COLORANT_BATCH_FOR_MULTIPLE_CANS] !== undefined) {
        if (typeof data[settings.COLORANT_BATCH_FOR_MULTIPLE_CANS] === "boolean") {
            settingState.colorantBatchForMultipleCans = data[settings.COLORANT_BATCH_FOR_MULTIPLE_CANS];
        }
    }

    if (data[settings.QUALITY_CONTROL_COLOR_DIFFERENCE] !== undefined) {
        if (typeof data[settings.QUALITY_CONTROL_COLOR_DIFFERENCE] === "string") {
            settingState.qualityControlDifference = data[settings.QUALITY_CONTROL_COLOR_DIFFERENCE];
        }
    }

    if (data[settings.QUALITY_CONTROL_DELTA_A] !== undefined) {
        if (typeof data[settings.QUALITY_CONTROL_DELTA_A] === "number") {
            settingState.qualityControlDeltaA = data[settings.QUALITY_CONTROL_DELTA_A];
        }
    }

    if (data[settings.QUALITY_CONTROL_DELTA_B] !== undefined) {
        if (typeof data[settings.QUALITY_CONTROL_DELTA_B] === "number") {
            settingState.qualityControlDeltaB = data[settings.QUALITY_CONTROL_DELTA_B];
        }
    }

    if (data[settings.QUALITY_CONTROL_DELTA_E] !== undefined) {
        if (typeof data[settings.QUALITY_CONTROL_DELTA_E] === "number") {
            settingState.qualityControlDeltaE = data[settings.QUALITY_CONTROL_DELTA_E];
        }
    }

    if (data[settings.QUALITY_CONTROL_DELTA_L] !== undefined) {
        if (typeof data[settings.QUALITY_CONTROL_DELTA_L] === "number") {
            settingState.qualityControlDeltaL = data[settings.QUALITY_CONTROL_DELTA_L];
        }
    }

    if (data[settings.QUALITY_CONTROL_METAMERISM] !== undefined) {
        if (typeof data[settings.QUALITY_CONTROL_METAMERISM] === "number") {
            settingState.qualityControlMetamerism = data[settings.QUALITY_CONTROL_METAMERISM];
        }
    }

    if (data[settings.QUALITY_CONTROL_ENABLED] !== undefined) {
        if (typeof data[settings.QUALITY_CONTROL_ENABLED] === "boolean") {
            settingState.qualityControlEnabled = data[settings.QUALITY_CONTROL_ENABLED];
        }
    }

    if (data[settings.ALTERNATIVE_FORMULAS_POSITION] !== undefined) {
        if (
            typeof data[settings.ALTERNATIVE_FORMULAS_POSITION] === "string" ||
            data[settings.ALTERNATIVE_FORMULAS_POSITION] === null
        ) {
            settingState.alternativeFormulasPosition = data[settings.ALTERNATIVE_FORMULAS_POSITION];
        }
    }

    if (data[settings.AUTOMATIC_CLIENT_START] !== undefined) {
        if (typeof data[settings.AUTOMATIC_CLIENT_START] === "boolean") {
            settingState.automaticClientStart = data[settings.AUTOMATIC_CLIENT_START];
        }
    }

    if (data[settings.AUTOMATIC_LOGOUT] !== undefined) {
        if (typeof data[settings.AUTOMATIC_LOGOUT] === "boolean") {
            settingState.automaticLogout = data[settings.AUTOMATIC_LOGOUT];
        }
    }

    if (data[settings.AUTOMATIC_LOGOUT_TIMEOUT] !== undefined) {
        if (typeof data[settings.AUTOMATIC_LOGOUT_TIMEOUT] === "number") {
            settingState.automaticLogoutTimeout = data[settings.AUTOMATIC_LOGOUT_TIMEOUT];
        }
    }

    if (data[settings.AUTOMATIC_PRINT] !== undefined) {
        if (typeof data[settings.AUTOMATIC_PRINT] === "boolean") {
            settingState.automaticPrint = data[settings.AUTOMATIC_PRINT];
        }
    }

    if (data[settings.PRINT_ENABLED] !== undefined) {
        if (typeof data[settings.PRINT_ENABLED] === "boolean") {
            settingState.printEnabled = data[settings.PRINT_ENABLED];
        }
    }

    if (data[settings.BARCODES_ENABLED] !== undefined) {
        if (typeof data[settings.BARCODES_ENABLED] === "boolean") {
            settingState.barcodesEnabled = data[settings.BARCODES_ENABLED];
        }
    }

    if (data[settings.COLORANT_BATCH_NUMBER_LOGIC] !== undefined) {
        if (typeof data[settings.COLORANT_BATCH_NUMBER_LOGIC] === "number") {
            settingState.colorantBatchNumberLogic = data[settings.COLORANT_BATCH_NUMBER_LOGIC];
        }
    }

    if (data[settings.BLEND_FORMULA_AND_CODE] !== undefined) {
        if (typeof data[settings.BLEND_FORMULA_AND_CODE] === "boolean") {
            settingState.blendFormulaAndCode = data[settings.BLEND_FORMULA_AND_CODE];
        }
    }

    if (data[settings.COLOR_PREVIEW_AS_IMAGE] !== undefined) {
        if (typeof data[settings.COLOR_PREVIEW_AS_IMAGE] === "boolean") {
            settingState.colorPreviewAsImage = data[settings.COLOR_PREVIEW_AS_IMAGE];
        }
    }

    if (data[settings.COLOR_PREVIEW_RESIZABLE] !== undefined) {
        if (
            typeof data[settings.COLOR_PREVIEW_RESIZABLE] === "string" ||
            data[settings.COLOR_PREVIEW_RESIZABLE] === null
        ) {
            settingState.colorPreviewResizable = data[settings.COLOR_PREVIEW_RESIZABLE];
        }
    }

    if (data[settings.COLOR_TAB_COMPATIBLE_ONLY] !== undefined) {
        if (typeof data[settings.COLOR_TAB_COMPATIBLE_ONLY] === "boolean") {
            settingState.colorTabCompatibleOnly = data[settings.COLOR_TAB_COMPATIBLE_ONLY];
        }
    }

    if (data[settings.COLOR_TAB_FORMULA_NAME] !== undefined) {
        if (typeof data[settings.COLOR_TAB_FORMULA_NAME] === "boolean") {
            settingState.colorTabFormulaName = data[settings.COLOR_TAB_FORMULA_NAME];
        }
    }

    if (data[settings.COLOR_TAB_FORMULA_CODE] !== undefined) {
        if (typeof data[settings.COLOR_TAB_FORMULA_CODE] === "boolean") {
            settingState.colorTabFormulaCode = data[settings.COLOR_TAB_FORMULA_CODE];
        }
    }

    if (data[settings.COLOR_TAB_NOTE_ICONS] !== undefined) {
        if (typeof data[settings.COLOR_TAB_NOTE_ICONS] === "boolean") {
            settingState.colorTabNoteIcons = data[settings.COLOR_TAB_NOTE_ICONS];
        }
    }

    if (data[settings.COLOR_TAB_ORDER_BY] !== undefined) {
        if (typeof data[settings.COLOR_TAB_ORDER_BY] === "string") {
            settingState.colorTabOrderBy = data[settings.COLOR_TAB_ORDER_BY];
        }
    }

    if (data[settings.COLOR_TAB_ROW_COUNT] !== undefined) {
        if (typeof data[settings.COLOR_TAB_ROW_COUNT] === "number") {
            settingState.colorTabRowCount = data[settings.COLOR_TAB_ROW_COUNT];
        }
    }

    if (data[settings.COLOR_TAB_WARNING_ICON] !== undefined) {
        if (typeof data[settings.COLOR_TAB_WARNING_ICON] === "boolean") {
            settingState.colorTabWarningIcon = data[settings.COLOR_TAB_WARNING_ICON];
        }
    }

    if (data[settings.COLORANT_PANEL_LIMIT] !== undefined) {
        if (typeof data[settings.COLORANT_PANEL_LIMIT] === "number") {
            settingState.colorantPanelLimit = data[settings.COLORANT_PANEL_LIMIT];
        }
    }

    if (data[settings.COLORANT_PANEL_ORDER_BY] !== undefined) {
        if (typeof data[settings.COLORANT_PANEL_ORDER_BY] === "string") {
            settingState.colorantPanelOrderBy = data[settings.COLORANT_PANEL_ORDER_BY];
        }
    }

    if (data[settings.COMPETITIVE_COLOR_SEARCH] !== undefined) {
        if (typeof data[settings.COMPETITIVE_COLOR_SEARCH] === "boolean") {
            settingState.competitiveColorSearch = data[settings.COMPETITIVE_COLOR_SEARCH];
        }
    }

    if (data[settings.CUSTOM_BANNER_POSITION] !== undefined) {
        if (typeof data[settings.CUSTOM_BANNER_POSITION] === "string" || data[settings.CUSTOM_HOME_BUTTON] === null) {
            settingState.customBannerPosition = data[settings.CUSTOM_BANNER_POSITION];
        }
    }

    if (data[settings.CUSTOM_CAN_OPTION] !== undefined) {
        if (typeof data[settings.CUSTOM_CAN_OPTION] === "string") {
            settingState.customCanOption = data[settings.CUSTOM_CAN_OPTION];
        }
    }

    if (data[settings.CUSTOM_CAN_SETTINGS_UNIT_GRAV] !== undefined) {
        if (
            typeof data[settings.CUSTOM_CAN_SETTINGS_UNIT_GRAV] === "string" ||
            data[settings.CUSTOM_CAN_SETTINGS_UNIT_GRAV] === null
        ) {
            settingState.customCanSettingsUnitGrav = data[settings.CUSTOM_CAN_SETTINGS_UNIT_GRAV];
        }
    }

    if (data[settings.CUSTOM_CAN_SETTINGS_UNIT_VOL] !== undefined) {
        if (
            typeof data[settings.CUSTOM_CAN_SETTINGS_UNIT_VOL] === "string" ||
            data[settings.CUSTOM_CAN_SETTINGS_UNIT_VOL] === null
        ) {
            settingState.customCanSettingsUnitVol = data[settings.CUSTOM_CAN_SETTINGS_UNIT_VOL];
        }
    }

    if (data[settings.CUSTOM_CAN_SIZE] !== undefined) {
        if (typeof data[settings.CUSTOM_CAN_SIZE] === "boolean") {
            settingState.customCanSizeEnabled = data[settings.CUSTOM_CAN_SIZE];
        }
    }

    if (data[settings.CUSTOM_CAN_UNITS] !== undefined) {
        if (Array.isArray(data[settings.CUSTOM_CAN_UNITS])) {
            settingState.customCanUnits = data[settings.CUSTOM_CAN_UNITS];
        }
    }

    if (data[settings.CUSTOM_FORMAT_CURRENCY] !== undefined) {
        if (
            typeof data[settings.CUSTOM_FORMAT_CURRENCY] === "string" ||
            data[settings.CUSTOM_FORMAT_CURRENCY] === null
        ) {
            settingState.customFormatCurrency = data[settings.CUSTOM_FORMAT_CURRENCY];
        }
    }

    if (data[settings.CUSTOM_FORMAT_DATE] !== undefined) {
        if (typeof data[settings.CUSTOM_FORMAT_DATE] === "string" || data[settings.CUSTOM_FORMAT_DATE] === null) {
            settingState.customFormatDate = data[settings.CUSTOM_FORMAT_DATE];
        }
    }

    if (data[settings.CUSTOM_FORMAT_NUMBER] !== undefined) {
        if (typeof data[settings.CUSTOM_FORMAT_NUMBER] === "string" || data[settings.CUSTOM_FORMAT_NUMBER] === null) {
            settingState.customFormatNumber = data[settings.CUSTOM_FORMAT_NUMBER];
        }
    }

    if (data[settings.CUSTOM_FORMAT_TIME] !== undefined) {
        if (typeof data[settings.CUSTOM_FORMAT_TIME] === "string" || data[settings.CUSTOM_FORMAT_TIME] === null) {
            settingState.customFormatTime = data[settings.CUSTOM_FORMAT_TIME];
        }
    }

    if (data[settings.CUSTOM_FORMULA_FOR_MASTER_PRODUCT] !== undefined) {
        if (typeof data[settings.CUSTOM_FORMULA_FOR_MASTER_PRODUCT] === "boolean") {
            settingState.customFormulaForMasterProduct = data[settings.CUSTOM_FORMULA_FOR_MASTER_PRODUCT];
        }
    }

    if (data[settings.CUSTOM_FORMULA_FOR_MASTER_PRODUCT_DEFAULT] !== undefined) {
        if (typeof data[settings.CUSTOM_FORMULA_FOR_MASTER_PRODUCT_DEFAULT] === "boolean") {
            settingState.customFormulaForMasterProductDefault =
                data[settings.CUSTOM_FORMULA_FOR_MASTER_PRODUCT_DEFAULT];
        }
    }

    if (data[settings.CUSTOM_FORMULAS] !== undefined) {
        if (typeof data[settings.CUSTOM_FORMULAS] === "boolean") {
            settingState.customFormulas = data[settings.CUSTOM_FORMULAS];
        }
    }

    if (data[settings.CUSTOM_HOME_BUTTON] !== undefined) {
        if (typeof data[settings.CUSTOM_HOME_BUTTON] === "string" || data[settings.CUSTOM_HOME_BUTTON] === null) {
            settingState.customHomeButton = data[settings.CUSTOM_HOME_BUTTON];
        }
    }

    if (data[settings.HISTORICAL_FORMULAS_POSITION] !== undefined) {
        if (
            typeof data[settings.HISTORICAL_FORMULAS_POSITION] === "string" ||
            data[settings.HISTORICAL_FORMULAS_POSITION] === null
        ) {
            settingState.historicalFormulasPosition = data[settings.HISTORICAL_FORMULAS_POSITION];
        }
    }

    if (data[settings.CUSTOMERS_ENABLED] !== undefined) {
        if (typeof data[settings.CUSTOMERS_ENABLED] === "boolean") {
            settingState.customersEnabled = data[settings.CUSTOMERS_ENABLED];
        }
    }

    if (data[settings.DELETE_CONFIRMATION] !== undefined) {
        if (typeof data[settings.DELETE_CONFIRMATION] === "boolean") {
            settingState.deleteConfirmation = data[settings.DELETE_CONFIRMATION];
        }
    }

    if (data[settings.DISK_SPACE_NOTIFICATION] !== undefined) {
        if (typeof data[settings.DISK_SPACE_NOTIFICATION] === "boolean") {
            settingState.diskSpaceNotification = data[settings.DISK_SPACE_NOTIFICATION];
        }
    }

    if (data[settings.DISPENSING_ENABLED] !== undefined) {
        if (typeof data[settings.DISPENSING_ENABLED] === "boolean") {
            settingState.dispensingEnabled = data[settings.DISPENSING_ENABLED];
        }
    }

    if (data[settings.DISPENSING_NOTES] !== undefined) {
        if (typeof data[settings.DISPENSING_NOTES] === "boolean") {
            settingState.dispensingNotes = data[settings.DISPENSING_NOTES];
        }
    }

    if (data[settings.DISPENSING_WITH_ERRORS_ENABLED] !== undefined) {
        if (typeof data[settings.DISPENSING_WITH_ERRORS_ENABLED] === "boolean") {
            settingState.dispensingWithErrorsEnabled = data[settings.DISPENSING_WITH_ERRORS_ENABLED];
        }
    }

    if (data[settings.EDIT_COLORANT_ALLOWANCE] !== undefined) {
        if (typeof data[settings.EDIT_COLORANT_ALLOWANCE] === "boolean") {
            settingState.editColorantAllowance = data[settings.EDIT_COLORANT_ALLOWANCE];
        }
    }

    if (data[settings.FANDECK_GROUPS] !== undefined) {
        if (typeof data[settings.FANDECK_GROUPS] === "boolean") {
            settingState.fandeckGroups = data[settings.FANDECK_GROUPS];
        }
    }

    if (data[settings.FILE_TRANSFER] !== undefined) {
        if (typeof data[settings.FILE_TRANSFER] === "boolean") {
            settingState.fileTransfer = data[settings.FILE_TRANSFER];
        }
    }

    if (data[settings.FILE_TRANSFER_IN_TASKS] !== undefined) {
        if (Array.isArray(data[settings.FILE_TRANSFER_IN_TASKS])) {
            settingState.fileTransferInTasks = data[settings.FILE_TRANSFER_IN_TASKS];
        }
    }

    if (data[settings.FILE_TRANSFER_OUT_TASKS] !== undefined) {
        if (Array.isArray(data[settings.FILE_TRANSFER_OUT_TASKS])) {
            settingState.fileTransferOutTasks = data[settings.FILE_TRANSFER_OUT_TASKS];
        }
    }

    if (data[settings.FORMULA_AUTOMATIC_NAMING] !== undefined) {
        if (typeof data[settings.FORMULA_AUTOMATIC_NAMING] === "boolean") {
            settingState.formulaAutomaticNaming = validationHelper.parseBooleanValue(
                data[settings.FORMULA_AUTOMATIC_NAMING]
            );
        }
    }

    if (data[settings.FORMULA_CODE] !== undefined) {
        if (typeof data[settings.FORMULA_CODE] === "boolean") {
            settingState.formulaCode = validationHelper.parseBooleanValue(data[settings.FORMULA_CODE]);
        }
    }

    if (data[settings.FORMULA_CODE_PRIMARY] !== undefined) {
        if (typeof data[settings.FORMULA_CODE_PRIMARY] === "boolean") {
            settingState.formulaCodePrimary = validationHelper.parseBooleanValue(data[settings.FORMULA_CODE_PRIMARY]);
        }
    }

    if (data[settings.FORMULA_COLOR_PREVIEW] !== undefined) {
        if (typeof data[settings.FORMULA_COLOR_PREVIEW] === "boolean") {
            settingState.formulaColorPreview = validationHelper.parseBooleanValue(data[settings.FORMULA_COLOR_PREVIEW]);
        }
    }

    if (data[settings.FORMULA_INFO_DATA] !== undefined) {
        if (Array.isArray(data[settings.FORMULA_INFO_DATA])) {
            settingState.formulaInfoData = data[settings.FORMULA_INFO_DATA];
        }
    }

    if (data[settings.FORMULA_MODIFICATION_PANEL] !== undefined) {
        if (typeof data[settings.FORMULA_MODIFICATION_PANEL] === "boolean") {
            settingState.formulaModificationPanel = validationHelper.parseBooleanValue(
                data[settings.FORMULA_MODIFICATION_PANEL]
            );
        }
    }

    if (data[settings.FORMULA_NAME] !== undefined) {
        if (typeof data[settings.FORMULA_NAME] === "boolean") {
            settingState.formulaName = validationHelper.parseBooleanValue(data[settings.FORMULA_NAME]);
        }
    }

    if (typeof data[settings.FORMULA_PREVIEW] === "string") {
        if (
            data[settings.FORMULA_PREVIEW] === settings.PREVIEW_COLOR ||
            data[settings.FORMULA_PREVIEW] === settings.PREVIEW_IMAGE ||
            data[settings.FORMULA_PREVIEW] === settings.PREVIEW_MORE_INFO
        ) {
            settingState.formulaPreview = data[settings.FORMULA_PREVIEW];
        }
    }

    if (data[settings.FORMULA_PROPERTIES_POSITION] !== undefined) {
        if (
            typeof data[settings.FORMULA_PROPERTIES_POSITION] === "string" ||
            data[settings.FORMULA_PROPERTIES_POSITION] === null
        ) {
            settingState.formulaPropertiesPosition = data[settings.FORMULA_PROPERTIES_POSITION];
        }
    }

    if (data[settings.FORMULA_STATUS_PANEL] !== undefined) {
        if (typeof data[settings.FORMULA_STATUS_PANEL] === "boolean") {
            settingState.formulaStatusPanel = validationHelper.parseBooleanValue(data[settings.FORMULA_STATUS_PANEL]);
        }
    }

    if (data[settings.FORMULA_UNITS] !== undefined) {
        if (typeof data[settings.FORMULA_UNITS] === "string") {
            if (machineType === rcc.DRIVER_TYPE_MANUAL || machineType === rcc.DRIVER_TYPE_MANUAL_SIMPLE) {
                settingState.formulaUnits = data[settings.FORMULA_UNITS];
            } else {
                settingState.formulaUnits =
                    data[settings.FORMULA_UNITS] === settings.UNIT_MANUAL
                        ? settings.UNIT_ML
                        : data[settings.FORMULA_UNITS];
            }
        }
    }

    if (data[settings.FTS] !== undefined) {
        if (typeof data[settings.FTS] === "boolean") {
            settingState.fts = data[settings.FTS];
        }
    }

    if (data[settings.GENERATE_COLOR_PREVIEW] !== undefined) {
        if (typeof data[settings.GENERATE_COLOR_PREVIEW] === "boolean") {
            settingState.generateColorPreview = data[settings.GENERATE_COLOR_PREVIEW];
        }
    }

    if (data[settings.GDPR_ENABLED] !== undefined) {
        if (typeof data[settings.GDPR_ENABLED] === "boolean") {
            settingState.gdprEnabled = data[settings.GDPR_ENABLED];
        }
    }

    if (data[settings.HELP] !== undefined) {
        if (typeof data[settings.HELP].telephone === "string") {
            settingState.helpSettings.telephone = data[settings.HELP].telephone;
        }
        if (typeof data[settings.HELP].email === "string") {
            settingState.helpSettings.email = data[settings.HELP].email;
        }

        if (typeof data[settings.HELP].message === "string") {
            if (settingState.helpSettings.message === "") {
                settingState.helpSettings.message = data[settings.HELP].message;
            }
        }
    }

    if (data.help_link !== undefined && typeof data.help_link === "string") {
        if (data.help_link !== "") {
            settingState.helpLink = data.help_link;
        }
    }

    if (data[settings.HELPER_AUTO_CHECK] !== undefined) {
        if (typeof data[settings.HELPER_AUTO_CHECK] === "boolean") {
            settingState.helperAutoCheck = data[settings.HELPER_AUTO_CHECK];
        }
    }

    if (data[settings.HELPER_AUTO_CHECK_INTERVAL] !== undefined) {
        if (typeof data[settings.HELPER_AUTO_CHECK_INTERVAL] === "number") {
            settingState.helperAutoCheckInterval = data[settings.HELPER_AUTO_CHECK_INTERVAL];
        }
    }

    if (data[settings.HELPER_AUTO_DOWNLOAD] !== undefined) {
        if (typeof data[settings.HELPER_AUTO_DOWNLOAD] === "boolean") {
            settingState.helperAutoDownload = data[settings.HELPER_AUTO_DOWNLOAD];
        }
    }

    if (data[settings.HELPER_AUTO_FORCE_INSTALL] !== undefined) {
        if (typeof data[settings.HELPER_AUTO_FORCE_INSTALL] === "boolean") {
            settingState.helperAutoForceInstall = data[settings.HELPER_AUTO_FORCE_INSTALL];
        }
    }

    if (data[settings.HELPER_AUTO_INSTALL] !== undefined) {
        if (typeof data[settings.HELPER_AUTO_INSTALL] === "boolean") {
            settingState.helperAutoInstall = data[settings.HELPER_AUTO_INSTALL];
        }
    }

    if (data[settings.HELPER_AUTO_INSTALL_DELAY] !== undefined) {
        if (typeof data[settings.HELPER_AUTO_INSTALL_DELAY] === "number") {
            settingState.helperAutoInstallDelay = data[settings.HELPER_AUTO_INSTALL_DELAY];
        }
    }

    if (data[settings.HELPER_AUTO_INSTALL_NEW_DBS] !== undefined) {
        if (typeof data[settings.HELPER_AUTO_INSTALL_NEW_DBS] === "boolean") {
            settingState.helperAutoInstallNewDbs = data[settings.HELPER_AUTO_INSTALL_NEW_DBS];
        }
    }

    if (data[settings.HELPER_AUTO_NIGHT_CHECK] !== undefined) {
        if (typeof data[settings.HELPER_AUTO_NIGHT_CHECK] === "boolean") {
            settingState.helperAutoNightCheck = data[settings.HELPER_AUTO_NIGHT_CHECK];
        }
    }

    if (data[settings.HELPER_AUTO_REBOOT_DELAY] !== undefined) {
        if (typeof data[settings.HELPER_AUTO_REBOOT_DELAY] === "number") {
            settingState.helperAutoRebootDelay = data[settings.HELPER_AUTO_REBOOT_DELAY];
        }
    }

    if (data[settings.IMAGES_SOURCE] !== undefined) {
        if (typeof data[settings.IMAGES_SOURCE] === "string") {
            settingState.imagesSource = data[settings.IMAGES_SOURCE];
        }
    }

    if (data[settings.INFO_PANEL_RESIZABLE] !== undefined) {
        if (typeof data[settings.INFO_PANEL_RESIZABLE] === "string" || data[settings.INFO_PANEL_RESIZABLE] === null) {
            settingState.infoPanelResizable = data[settings.INFO_PANEL_RESIZABLE];
        }
    }

    if (typeof data[settings.INFO_PANEL_TAB] === "string") {
        if (
            data[settings.INFO_PANEL_TAB] &&
            (data[settings.INFO_PANEL_TAB] === settings.INFO_PANEL_PACKAGE ||
                data[settings.INFO_PANEL_TAB] === settings.INFO_PANEL_MORE)
        ) {
            settingState.infoPanelTab = data[settings.INFO_PANEL_TAB];
        } else {
            settingState.infoPanelTab = settings.INFO_PANEL_PACKAGE;
        }
    }

    if (typeof data[settings.INIT_TAB_TYPE] === "string") {
        if (
            data[settings.INIT_TAB_TYPE] === componentTypes.INITIAL_TAB ||
            data[settings.INIT_TAB_TYPE] === componentTypes.FAVORITES_TAB ||
            data[settings.INIT_TAB_TYPE] === componentTypes.MAIN_TAB ||
            data[settings.INIT_TAB_TYPE] === componentTypes.COLOR_TAB ||
            data[settings.INIT_TAB_TYPE] === componentTypes.COLOR_SEARCH_TAB
        ) {
            settingState.initialTabType = data[settings.INIT_TAB_TYPE];
        }
    }

    if (typeof data[settings.KEYBOARD] === "string") {
        if (
            data[settings.KEYBOARD] === languages.ENGLISH ||
            data[settings.KEYBOARD] === languages.DUTCH ||
            data[settings.KEYBOARD] === languages.BELGIAN
        ) {
            settingState.keyboard = data[settings.KEYBOARD];
        }
    }

    if (data[settings.KEYBOARD_SHORTCUTS] !== undefined) {
        settingState.keyboardShortcuts = getKeyboardShortcuts(data[settings.KEYBOARD_SHORTCUTS]);
    }

    if (data[settings.KIOSK] !== undefined) {
        if (typeof data[settings.KIOSK] === "boolean") {
            settingState.kiosk = data[settings.KIOSK];
        }
    }
    if (data[settings.LIGHT_ENABLED] !== undefined) {
        if (typeof data[settings.LIGHT_ENABLED] === "boolean") {
            settingState.lightEnabled = data[settings.LIGHT_ENABLED];
        }
    }

    if (data[settings.LOCALE] !== undefined) {
        if (typeof data[settings.LOCALE] === "string" || data[settings.LOCALE] === null) {
            settingState.locale = data[settings.LOCALE] === null ? data[settings.LANGUAGE] : data[settings.LOCALE];

            // Compatibility with previous versions
            if (settingState.locale === languages.ENGLISH) {
                settingState.locale = languages.ENGLISH_GB;
            }

            if (settingState.locale === languages.CHINESE) {
                settingState.locale = languages.CHINESE_CHINA;
            }
        }
    }

    if (data[settings.LOCK_COMPUTER] !== undefined) {
        if (typeof data[settings.LOCK_COMPUTER] === "boolean") {
            settingState.lockComputer = data[settings.LOCK_COMPUTER];
        }
    }

    if (data[settings.MASTER_PRODUCT_NAME] !== undefined) {
        if (typeof data[settings.MASTER_PRODUCT_NAME] === "boolean") {
            settingState.masterProductName = data[settings.MASTER_PRODUCT_NAME];
        }
    }

    if (data[settings.MASTER_PRODUCT_SIBLINGS_TABLE] !== undefined) {
        if (typeof data[settings.MASTER_PRODUCT_SIBLINGS_TABLE] === "boolean") {
            settingState.masterProductSiblingsTable = data[settings.MASTER_PRODUCT_SIBLINGS_TABLE];
        }
    }

    if (data[settings.MATCHING_INFO_CR] !== undefined) {
        if (typeof data[settings.MATCHING_INFO_CR] === "boolean") {
            settingState.matchingInfoCr = data[settings.MATCHING_INFO_CR];
        }
    }

    if (data[settings.MATCHING_INFO_DELTA_E1] !== undefined) {
        if (typeof data[settings.MATCHING_INFO_DELTA_E1] === "boolean") {
            settingState.matchingInfoDeltaE1 = data[settings.MATCHING_INFO_DELTA_E1];
        }
    }

    if (data[settings.MATCHING_INFO_DELTA_E2] !== undefined) {
        if (typeof data[settings.MATCHING_INFO_DELTA_E2] === "boolean") {
            settingState.matchingInfoDeltaE2 = data[settings.MATCHING_INFO_DELTA_E2];
        }
    }

    if (data[settings.MATCHING_INFO_DELTA_E3] !== undefined) {
        if (typeof data[settings.MATCHING_INFO_DELTA_E3] === "boolean") {
            settingState.matchingInfoDeltaE3 = data[settings.MATCHING_INFO_DELTA_E3];
        }
    }

    if (data[settings.MATCHING_INFO_DIN] !== undefined) {
        if (typeof data[settings.MATCHING_INFO_DIN] === "boolean") {
            settingState.matchingInfoDin = data[settings.MATCHING_INFO_DIN];
        }
    }

    if (data[settings.MATCHING_INFO_LRV] !== undefined) {
        if (typeof data[settings.MATCHING_INFO_LRV] === "boolean") {
            settingState.matchingInfoLrv = data[settings.MATCHING_INFO_LRV];
        }
    }

    if (data[settings.MATCHING_INFO_RMS] !== undefined) {
        if (typeof data[settings.MATCHING_INFO_RMS] === "boolean") {
            settingState.matchingInfoRms = data[settings.MATCHING_INFO_RMS];
        }
    }

    if (data[settings.MATCHING_OPTIONS_COLORANTS] !== undefined) {
        if (typeof data[settings.MATCHING_OPTIONS_COLORANTS] === "boolean") {
            settingState.matchingOptionsColorants = data[settings.MATCHING_OPTIONS_COLORANTS];
        }
    }

    if (data[settings.MATCHING_OPTIONS_COLORFILE] !== undefined) {
        if (typeof data[settings.MATCHING_OPTIONS_COLORFILE] === "boolean") {
            settingState.matchingOptionsColorfile = data[settings.MATCHING_OPTIONS_COLORFILE];
        }
    }

    if (data[settings.MATCHING_OPTIONS_SORT_RESULTS] !== undefined) {
        if (typeof data[settings.MATCHING_OPTIONS_SORT_RESULTS] === "boolean") {
            settingState.matchingOptionsSortResults = data[settings.MATCHING_OPTIONS_SORT_RESULTS];
        }
    }

    if (data[settings.MATCHING_ROW_COUNT] !== undefined) {
        if (typeof data[settings.MATCHING_ROW_COUNT] === "number") {
            settingState.matchingRowCount = data[settings.MATCHING_ROW_COUNT];
        }
    }

    if (data[settings.MATCHING_TABLE_BASE] !== undefined) {
        if (typeof data[settings.MATCHING_TABLE_BASE] === "boolean") {
            settingState.matchingTableBase = data[settings.MATCHING_TABLE_BASE];
        }
    }

    if (data[settings.TABLE_CLIENT_NAME] !== undefined) {
        if (typeof data[settings.TABLE_CLIENT_NAME] === "boolean") {
            settingState.tableClientName = data[settings.TABLE_CLIENT_NAME];
        }
    }

    if (data[settings.MATCHING_TABLE_COLORANTS] !== undefined) {
        if (typeof data[settings.MATCHING_TABLE_COLORANTS] === "boolean") {
            settingState.matchingTableColorants = data[settings.MATCHING_TABLE_COLORANTS];
        }
    }

    if (data[settings.MATCHING_TABLE_CR] !== undefined) {
        if (typeof data[settings.MATCHING_TABLE_CR] === "boolean") {
            settingState.matchingTableCr = data[settings.MATCHING_TABLE_CR];
        }
    }

    if (data[settings.MATCHING_TABLE_DELTA_E1] !== undefined) {
        if (typeof data[settings.MATCHING_TABLE_DELTA_E1] === "boolean") {
            settingState.matchingTableDeltaE1 = data[settings.MATCHING_TABLE_DELTA_E1];
        }
    }

    if (data[settings.MATCHING_TABLE_DELTA_E2] !== undefined) {
        if (typeof data[settings.MATCHING_TABLE_DELTA_E2] === "boolean") {
            settingState.matchingTableDeltaE2 = data[settings.MATCHING_TABLE_DELTA_E2];
        }
    }

    if (data[settings.MATCHING_TABLE_DELTA_E3] !== undefined) {
        if (typeof data[settings.MATCHING_TABLE_DELTA_E3] === "boolean") {
            settingState.matchingTableDeltaE3 = data[settings.MATCHING_TABLE_DELTA_E3];
        }
    }

    if (data[settings.MATCHING_TABLE_DIN] !== undefined) {
        if (typeof data[settings.MATCHING_TABLE_DIN] === "boolean") {
            settingState.matchingTableDin = data[settings.MATCHING_TABLE_DIN];
        }
    }

    if (data[settings.MATCHING_TABLE_LRV] !== undefined) {
        if (typeof data[settings.MATCHING_TABLE_LRV] === "boolean") {
            settingState.matchingTableLrv = data[settings.MATCHING_TABLE_LRV];
        }
    }

    if (data[settings.MATCHING_TABLE_PRICE] !== undefined) {
        if (typeof data[settings.MATCHING_TABLE_PRICE] === "boolean") {
            settingState.matchingTablePrice = data[settings.MATCHING_TABLE_PRICE];
        }
    }

    if (data[settings.MATCHING_TABLE_RMS] !== undefined) {
        if (typeof data[settings.MATCHING_TABLE_RMS] === "boolean") {
            settingState.matchingTableRms = data[settings.MATCHING_TABLE_RMS];
        }
    }

    if (data[settings.MAX_TOLERANCE_FOR_COMPETITIVE_COLORS] !== undefined) {
        if (typeof data[settings.MAX_TOLERANCE_FOR_COMPETITIVE_COLORS] === "number") {
            settingState.maxToleranceForCompetitiveColors = data[settings.MAX_TOLERANCE_FOR_COMPETITIVE_COLORS];
        }
    }

    if (data[settings.MORE_INFO_INTERNAL] !== undefined) {
        if (typeof data[settings.MORE_INFO_INTERNAL] === "boolean") {
            settingState.moreInfoInternal = data[settings.MORE_INFO_INTERNAL];
        }
    }

    if (data[settings.NAVIGATION_ITEMS] !== undefined && Array.isArray(data[settings.NAVIGATION_ITEMS])) {
        if (Array.isArray(options.navigationItems)) {
            for (let option of options.navigationItems) {
                if (getterHelper.getObjectById(data[settings.NAVIGATION_ITEMS], option) === null) {
                    data[settings.NAVIGATION_ITEMS].push({id: option, visible: false});
                }
            }
        }

        settingState.navigationItems = getNavigationItemsSettingsByLicense(
            data[settings.NAVIGATION_ITEMS],
            productPricing
        );
    }

    if (data[settings.NUMBER_OF_TABS] !== undefined) {
        if (typeof data[settings.NUMBER_OF_TABS] === "number") {
            settingState.maxNumberOfTabs = data[settings.NUMBER_OF_TABS];
        }
    }

    if (data[settings.NUMBER_OF_COMPETITIVE_COLORS] !== undefined) {
        if (typeof data[settings.NUMBER_OF_COMPETITIVE_COLORS] === "number") {
            settingState.numberOfCompetitiveColors = data[settings.NUMBER_OF_COMPETITIVE_COLORS];
        }
    }

    if (data[settings.FORMULA_NOTES] !== undefined) {
        if (typeof data[settings.FORMULA_NOTES] === "boolean") {
            settingState.formulaNotes = data[settings.FORMULA_NOTES];
        }
    }

    if (data[settings.ORDER_MODAL_ENABLED] !== undefined) {
        if (typeof data[settings.ORDER_MODAL_ENABLED] === "boolean") {
            settingState.orderModalEnabled = data[settings.ORDER_MODAL_ENABLED];
        }
    }

    if (data[settings.ORDER_SEARCH_ITEMS] !== undefined) {
        if (Array.isArray(data[settings.ORDER_SEARCH_ITEMS])) {
            settingState.orderSearchItemsEnabled = data[settings.ORDER_SEARCH_ITEMS];
        }
    }

    if (data[settings.ORDERING_ENABLED] !== undefined) {
        if (typeof data[settings.ORDERING_ENABLED] === "boolean") {
            settingState.orderingEnabled = data[settings.ORDERING_ENABLED];
        }
    }

    if (data[settings.OVERWRITE_HELPER_CONFIG] !== undefined) {
        if (typeof data[settings.OVERWRITE_HELPER_CONFIG] === "boolean") {
            settingState.overwriteHelperConfig = data[settings.OVERWRITE_HELPER_CONFIG];
        }
    }

    if (data[settings.OVERWRITE_LAST_TAB] !== undefined) {
        if (typeof data[settings.OVERWRITE_LAST_TAB] === "boolean") {
            settingState.overwriteLastTab = data[settings.OVERWRITE_LAST_TAB];
        }
    }

    if (data[settings.OVERWRITE_LOGO] !== undefined) {
        if (typeof data[settings.OVERWRITE_LOGO] === "boolean") {
            settingState.overwriteLogo = data[settings.OVERWRITE_LOGO];
        }
    }

    if (data[settings.OVERWRITE_MACHINE_CONFIG] !== undefined) {
        if (typeof data[settings.OVERWRITE_MACHINE_CONFIG] === "boolean") {
            settingState.overwriteMachineConfig = data[settings.OVERWRITE_MACHINE_CONFIG];
        }
    }

    if (data[settings.PARTIAL_UPDATE_INACTIVITY_TIMER] !== undefined) {
        if (typeof data[settings.PARTIAL_UPDATE_INACTIVITY_TIMER] === "number") {
            settingState.partialUpdatesInactivityTimer = data[settings.PARTIAL_UPDATE_INACTIVITY_TIMER];
        }
    }

    if (data[settings.PARTIAL_UPDATE_REMIND_INTERVAL] !== undefined) {
        if (typeof data[settings.PARTIAL_UPDATE_REMIND_INTERVAL] === "number") {
            settingState.partialUpdatesRemindInterval = data[settings.PARTIAL_UPDATE_REMIND_INTERVAL];
        }
    }

    if (data[settings.PRICE_TYPES] !== undefined && Array.isArray(data[settings.PRICE_TYPES])) {
        settingState.priceTypes = data[settings.PRICE_TYPES];
    }

    if (data[settings.PRICING_ENABLED] !== undefined) {
        if (typeof data[settings.PRICING_ENABLED] === "boolean") {
            settingState.pricingEnabled = data[settings.PRICING_ENABLED];
        }
    }

    if (data[settings.PRICE_VISIBILITY_TYPES] !== undefined && typeof data[settings.MARGIN_MARKUP] === "string") {
        if (
            data[settings.PRICE_VISIBILITY_TYPES] === settings.WITH_VAT_OPTION ||
            data[settings.PRICE_VISIBILITY_TYPES] === settings.WITHOUT_VAT_OPTION ||
            data[settings.PRICE_VISIBILITY_TYPES] === settings.WITH_AND_WITHOUT_VAT_OPTION
        ) {
            settingState.priceVisibilityType = data[settings.PRICE_VISIBILITY_TYPES];
        } else {
            settingState.priceVisibilityType = settings.WITH_AND_WITHOUT_VAT_OPTION;
        }
    }

    if (data[settings.PRINT_AFTER_DISPENSING] !== undefined) {
        if (typeof data[settings.PRINT_AFTER_DISPENSING] === "boolean") {
            settingState.printAfterDispensing = data[settings.PRINT_AFTER_DISPENSING];
        }
    }

    if (data[settings.PRINT_AS_IMAGE] !== undefined) {
        if (typeof data[settings.PRINT_AS_IMAGE] === "boolean") {
            settingState.printAsImage = data[settings.PRINT_AS_IMAGE];
        }
    }

    if (data[settings.PRINT_BEFORE_DISPENSING] !== undefined) {
        if (typeof data[settings.PRINT_BEFORE_DISPENSING] === "boolean") {
            settingState.printBeforeDispensing = data[settings.PRINT_BEFORE_DISPENSING];
        }
    }

    if (data[settings.PRINT_ON_REQUEST] !== undefined) {
        if (typeof data[settings.PRINT_ON_REQUEST] === "boolean") {
            settingState.printOnRequest = data[settings.PRINT_ON_REQUEST];
        }
    }

    if (data[settings.PRINT_TASKS_AFTER_DISPENSING] !== undefined) {
        if (Array.isArray(data[settings.PRINT_TASKS_AFTER_DISPENSING])) {
            settingState.printTasksAfterDispensing = data[settings.PRINT_TASKS_AFTER_DISPENSING];
        }
    }

    if (data[settings.PRINT_TASKS_BEFORE_DISPENSING] !== undefined) {
        if (Array.isArray(data[settings.PRINT_TASKS_BEFORE_DISPENSING])) {
            settingState.printTasksBeforeDispensing = data[settings.PRINT_TASKS_BEFORE_DISPENSING];
        }
    }

    if (data[settings.PRINTER] !== undefined) {
        if (typeof data[settings.PRINTER] === "string" || data[settings.PRINTER] === null) {
            settingState.printer = data[settings.PRINTER];
        }
    }

    if (data[settings.PRINTER_TEMPLATE] !== undefined) {
        if (typeof data[settings.PRINTER_TEMPLATE] === "string" || data[settings.PRINTER_TEMPLATE] === null) {
            settingState.printerTemplate = data[settings.PRINTER_TEMPLATE];
        }
    }

    if (data[settings.PRINTER_TEMPLATE_QUICKTINT] !== undefined) {
        if (
            typeof data[settings.PRINTER_TEMPLATE_QUICKTINT] === "string" ||
            data[settings.PRINTER_TEMPLATE_QUICKTINT] === null
        ) {
            settingState.printerTemplateQuickTint = data[settings.PRINTER_TEMPLATE_QUICKTINT];
        }
    }

    if (data[settings.PRODUCT_CODE] !== undefined) {
        if (typeof data[settings.PRODUCT_CODE] === "boolean") {
            settingState.productCode = data[settings.PRODUCT_CODE];
        }
    }

    if (data[settings.PRODUCT_GROUPS] !== undefined) {
        if (typeof data[settings.PRODUCT_GROUPS] === "boolean") {
            settingState.productGroups = data[settings.PRODUCT_GROUPS];
        }
    }

    if (data[settings.PRODUCT_FILES_SOURCE] !== undefined) {
        if (typeof data[settings.PRODUCT_FILES_SOURCE] === "string") {
            settingState.productFilesSource = data[settings.PRODUCT_FILES_SOURCE];
        }
    }

    if (data[settings.PRODUCT_IMAGE_PREVIEW] !== undefined) {
        if (typeof data[settings.PRODUCT_IMAGE_PREVIEW] === "boolean") {
            settingState.productImagePreview = data[settings.PRODUCT_IMAGE_PREVIEW];
        }
    }

    if (data[settings.PRODUCT_CODE] !== undefined) {
        if (typeof data[settings.PRODUCT_CODE] === "boolean") {
            settingState.productCode = data[settings.PRODUCT_CODE];
        }
    }

    if (data[settings.QUICK_TINT_SORTING_TYPE] !== undefined) {
        if (typeof data[settings.QUICK_TINT_SORTING_TYPE] === "string") {
            settingState.quickTintSortingType = data[settings.QUICK_TINT_SORTING_TYPE];
        }
    }

    if (data[settings.QUICK_TINT_WINDOW_CLOSE_AFTER_DISPENSING] !== undefined) {
        if (typeof data[settings.QUICK_TINT_WINDOW_CLOSE_AFTER_DISPENSING] === "boolean") {
            settingState.quickTintWindowCloseAfterDispensing = data[settings.QUICK_TINT_WINDOW_CLOSE_AFTER_DISPENSING];
        }
    }

    if (data[settings.ROUNDING] !== undefined) {
        if (typeof data[settings.ROUNDING] === "number") {
            settingState.rounding = isNaN(parseInt(data[settings.ROUNDING], 10))
                ? settings.DEFAULT_ROUNDING
                : parseInt(data[settings.ROUNDING], 10);
        }
    }

    if (data[settings.RCC_BARCODE_REFILL] !== undefined) {
        if (typeof data[settings.RCC_BARCODE_REFILL] === "string") {
            settingState.rccBarcodeRefill = data[settings.RCC_BARCODE_REFILL];
        }
    }

    if (data[settings.RCC_BASE_CAN_BARCODE_VALIDATION] !== undefined) {
        if (typeof data[settings.RCC_BASE_CAN_BARCODE_VALIDATION] === "boolean") {
            settingState.rccBaseCanBarcodeValidation = data[settings.RCC_BASE_CAN_BARCODE_VALIDATION];
        }
    }

    if (data[settings.RCC_CHECK_CAN_BEFORE_DISPENSE] !== undefined) {
        if (typeof data[settings.RCC_CHECK_CAN_BEFORE_DISPENSE] === "boolean") {
            settingState.rccCheckCanBeforeDispense = data[settings.RCC_CHECK_CAN_BEFORE_DISPENSE];
        }
    }

    if (data[settings.RCC_CHECK_OVERFILL] !== undefined) {
        if (typeof data[settings.RCC_CHECK_OVERFILL] === "boolean") {
            settingState.rccCheckOverfill = data[settings.RCC_CHECK_OVERFILL];
        }
    }

    if (data[settings.RCC_DISPENSE_INVALID_COLORANTS] !== undefined) {
        if (typeof data[settings.RCC_DISPENSE_INVALID_COLORANTS] === "boolean") {
            settingState.rccDispenseInvalidColorants = data[settings.RCC_DISPENSE_INVALID_COLORANTS];
        }
    }

    if (data[settings.RCC_MANUAL_COLORANT_ADDITION] !== undefined) {
        if (typeof data[settings.RCC_MANUAL_COLORANT_ADDITION] === "string") {
            settingState.rccManualColorantAddition = data[settings.RCC_MANUAL_COLORANT_ADDITION];
        }
    }

    if (data[settings.RCC_MINIMAL_DISPENSE_AMOUNT] !== undefined) {
        if (typeof data[settings.RCC_MINIMAL_DISPENSE_AMOUNT] === "number") {
            settingState.rccMinimalDispenseAmount = isNaN(Number(data[settings.RCC_MINIMAL_DISPENSE_AMOUNT]))
                ? 2
                : Number(data[settings.RCC_MINIMAL_DISPENSE_AMOUNT]);
        }
    }

    if (data[settings.RCC_PLACE_CAN_SENSOR] !== undefined) {
        if (typeof data[settings.RCC_PLACE_CAN_SENSOR] === "boolean") {
            settingState.rccPlaceCanSensor = data[settings.RCC_PLACE_CAN_SENSOR];
        }
    }

    if (data[settings.RCC_PLACE_CAN_SENSOR_DELAY] !== undefined) {
        if (typeof data[settings.RCC_PLACE_CAN_SENSOR_DELAY] === "number") {
            settingState.rccPlaceCanSensorDelay = isNaN(Number(data[settings.RCC_PLACE_CAN_SENSOR_DELAY]))
                ? 2
                : Number(data[settings.RCC_PLACE_CAN_SENSOR_DELAY]);
        }
    }

    if (data[settings.RCC_REMOVE_CAN_SENSOR] !== undefined) {
        if (typeof data[settings.RCC_REMOVE_CAN_SENSOR] === "boolean") {
            settingState.rccRemoveCanSensor = data[settings.RCC_REMOVE_CAN_SENSOR];
        }
    }

    if (data[settings.RCC_REMOVE_CAN_SENSOR_DELAY] !== undefined) {
        if (typeof data[settings.RCC_REMOVE_CAN_SENSOR_DELAY] === "number") {
            settingState.rccRemoveCanSensorDelay = isNaN(Number(data[settings.RCC_REMOVE_CAN_SENSOR_DELAY]))
                ? 2
                : Number(data[settings.RCC_REMOVE_CAN_SENSOR_DELAY]);
        }
    }

    if (data[settings.RCC_SKIP_PLACE_CAN_DIALOG] !== undefined) {
        if (typeof data[settings.RCC_SKIP_PLACE_CAN_DIALOG] === "boolean") {
            settingState.rccSkipPlaceCanDialog = data[settings.RCC_SKIP_PLACE_CAN_DIALOG];
        }
    }

    if (data[settings.RCC_SKIP_REMOVE_CAN_DIALOG] !== undefined) {
        if (typeof data[settings.RCC_SKIP_REMOVE_CAN_DIALOG] === "boolean") {
            settingState.rccSkipRemoveCanDialog = data[settings.RCC_SKIP_REMOVE_CAN_DIALOG];
        }
    }

    if (data[settings.RCC_SPLIT_FORMULA] !== undefined) {
        if (typeof data[settings.RCC_SPLIT_FORMULA] === "string") {
            settingState.rccSplitFormula = data[settings.RCC_SPLIT_FORMULA];
        }
    }

    if (data[settings.RCC_TMC_ENABLED] !== undefined) {
        if (typeof data[settings.RCC_TMC_ENABLED] === "boolean") {
            settingState.rccTmcEnabled = data[settings.RCC_TMC_ENABLED];
        }
    }

    if (data[settings.RCC_UNIT] !== undefined) {
        if (typeof data[settings.RCC_UNIT] === "string") {
            settingState.rccUnit = data[settings.RCC_UNIT];
        }
    }

    if (data[settings.RCC_VALIDATE_COLORANTS_DURING_INIT] !== undefined) {
        if (typeof data[settings.RCC_VALIDATE_COLORANTS_DURING_INIT] === "boolean") {
            settingState.rccValidateColorantsDuringInit = data[settings.RCC_VALIDATE_COLORANTS_DURING_INIT];
        }
    }

    if (data[settings.SEARCH_CHEAPER_COLORS] !== undefined) {
        if (typeof data[settings.SEARCH_CHEAPER_COLORS] === "boolean") {
            settingState.searchCheaperColors = data[settings.SEARCH_CHEAPER_COLORS];
        }
    }

    if (data[settings.SEARCH_PANEL_BASE] !== undefined) {
        if (typeof data[settings.SEARCH_PANEL_BASE] === "boolean") {
            settingState.searchPanelBase = data[settings.SEARCH_PANEL_BASE];
        }
    }

    if (data[settings.SEARCH_PANEL_BASE_SIZE] !== undefined) {
        if (typeof data[settings.SEARCH_PANEL_BASE_SIZE] === "number") {
            settingState.searchPanelBaseSize = data[settings.SEARCH_PANEL_BASE_SIZE];
        }
    }

    if (data[settings.SEARCH_PANEL_BIPP] !== undefined) {
        if (typeof data[settings.SEARCH_PANEL_BIPP] === "boolean") {
            settingState.searchPanelBipp = data[settings.SEARCH_PANEL_BIPP];
        }
    }

    if (data[settings.SEARCH_PANEL_BIPP_SIZE] !== undefined) {
        if (typeof data[settings.SEARCH_PANEL_BIPP_SIZE] === "number") {
            settingState.searchPanelBippSize = data[settings.SEARCH_PANEL_BIPP_SIZE];
        }
    }

    if (data[settings.SEARCH_PANEL_DROP_DOWN_CALCULATE] !== undefined) {
        if (typeof data[settings.SEARCH_PANEL_DROP_DOWN_CALCULATE] === "boolean") {
            settingState.searchPanelDropDownCalculate = data[settings.SEARCH_PANEL_DROP_DOWN_CALCULATE];
        }
    }

    if (data[settings.SEARCH_PANEL_FANDECK_SIZE] !== undefined) {
        if (typeof data[settings.SEARCH_PANEL_FANDECK_SIZE] === "number") {
            settingState.searchPanelFandeckSize = data[settings.SEARCH_PANEL_FANDECK_SIZE];
        }
    }

    if (data[settings.SEARCH_PANEL_FORMULA_SIZE] !== undefined) {
        if (typeof data[settings.SEARCH_PANEL_FORMULA_SIZE] === "number") {
            settingState.searchPanelFormulaSize = data[settings.SEARCH_PANEL_FORMULA_SIZE];
        }
    }

    if (data[settings.SEARCH_PANEL_ORDER_BY_DISTANCE] !== undefined) {
        if (typeof data[settings.SEARCH_PANEL_ORDER_BY_DISTANCE] === "boolean") {
            settingState.searchPanelOrderByDistance = data[settings.SEARCH_PANEL_ORDER_BY_DISTANCE];
        }
    }

    if (data[settings.SEARCH_PANEL_PRODUCT] !== undefined) {
        if (typeof data[settings.SEARCH_PANEL_PRODUCT] === "boolean") {
            settingState.searchPanelProduct = data[settings.SEARCH_PANEL_PRODUCT];
        }
    }

    if (data[settings.SEARCH_PANEL_PRODUCT_SIZE] !== undefined) {
        if (typeof data[settings.SEARCH_PANEL_PRODUCT_SIZE] === "number") {
            settingState.searchPanelProductSize = data[settings.SEARCH_PANEL_PRODUCT_SIZE];
        }
    }

    if (data[settings.SEARCH_PANEL_QUICK_SELECT] !== undefined) {
        if (typeof data[settings.SEARCH_PANEL_QUICK_SELECT] === "boolean") {
            settingState.searchPanelQuickSelect = data[settings.SEARCH_PANEL_QUICK_SELECT];

            if (gammaSoftware && data[settings.INTERGAMMA_ORDER_QUICK_SELECT] !== undefined) {
                if (
                    typeof data[settings.INTERGAMMA_ORDER_QUICK_SELECT] === "boolean" &&
                    data[settings.INTERGAMMA_ORDER_QUICK_SELECT] !== settingState.searchPanelQuickSelect
                ) {
                    settingState.searchPanelQuickSelect = data[settings.INTERGAMMA_ORDER_QUICK_SELECT];
                }
            }
        }
    }

    // Search panel order must be processed after processing of all search panel settings
    // because search panel order would use default values for items:
    // formulaName, formulaCode, blendFormulaAndCode, searchPanelBase, searchPanelBipp, searchPanelProduct
    if (data[settings.SEARCH_PANEL_ORDER] !== undefined) {
        if (Array.isArray(data[settings.SEARCH_PANEL_ORDER])) {
            settingState.searchPanelOrder = processSearchPanelOrderSetting(
                data[settings.SEARCH_PANEL_ORDER],
                settingState.formulaName,
                settingState.formulaCode,
                settingState.blendFormulaAndCode,
                settingState.searchPanelBase,
                settingState.searchPanelBipp,
                settingState.searchPanelProduct,
                gammaSoftware
            );
        }
    }

    if (data[settings.SCREENSAVER_TIMEOUT] !== undefined) {
        if (typeof data[settings.SCREENSAVER_TIMEOUT] === "number") {
            settingState.screensaverTimeout = data[settings.SCREENSAVER_TIMEOUT];
        }
    }

    if (data[settings.ACTIVE_FORMULA_UNIT] !== undefined) {
        if (typeof data[settings.ACTIVE_FORMULA_UNIT] === "string") {
            settingState.lastUsedUnit = data[settings.ACTIVE_FORMULA_UNIT];
        }
    }

    if (data[settings.FORMULA_INFO_ITEM_IN_RGB_PREVIEW] !== undefined) {
        if (
            typeof data[settings.FORMULA_INFO_ITEM_IN_RGB_PREVIEW] === "string" ||
            data[settings.FORMULA_INFO_ITEM_IN_RGB_PREVIEW] === null
        ) {
            settingState.formulaInfoItemInRgbPreview = data[settings.FORMULA_INFO_ITEM_IN_RGB_PREVIEW];
        }
    }

    if (data[settings.SHOTS_COEF] !== undefined) {
        if (typeof data[settings.SHOTS_COEF] === "number") {
            settingState.shotsCoef = data[settings.SHOTS_COEF];
        }
    }

    if (data[settings.SHOTS_NAME] !== undefined) {
        if (typeof data[settings.SHOTS_NAME] === "string") {
            settingState.shotsName = data[settings.SHOTS_NAME];
        }
    }

    if (data[settings.SOFTWARE_NAME] !== undefined) {
        if (typeof data[settings.SOFTWARE_NAME] === "string") {
            settingState.softwareName = data[settings.SOFTWARE_NAME];
        }
    }

    if (data[settings.SOFTWARE_NAME_LIGHT] !== undefined) {
        if (typeof data[settings.SOFTWARE_NAME_LIGHT] === "string") {
            settingState.softwareNameLight = data[settings.SOFTWARE_NAME_LIGHT];
        }
    }

    if (data[settings.VALIDATE_COLORANT_BASE_RULES] !== undefined) {
        if (typeof data[settings.VALIDATE_COLORANT_BASE_RULES] === "boolean") {
            settingState.validateColorantBaseRules = data[settings.VALIDATE_COLORANT_BASE_RULES];
        }
    }

    if (data[settings.VALIDATE_MIN_DOSABLE_AMOUNT] !== undefined) {
        if (typeof data[settings.VALIDATE_MIN_DOSABLE_AMOUNT] === "boolean") {
            settingState.validateMinDosableAmount = data[settings.VALIDATE_MIN_DOSABLE_AMOUNT];
        }
    }

    if (data[settings.VALIDATE_OVERFILL] !== undefined) {
        if (typeof data[settings.VALIDATE_OVERFILL] === "boolean") {
            settingState.validateOverfill = data[settings.VALIDATE_OVERFILL];
        }
    }

    if (data[settings.VALIDATE_TOTAL_CNT_AMOUNTS] !== undefined) {
        if (typeof data[settings.VALIDATE_TOTAL_CNT_AMOUNTS] === "boolean") {
            settingState.validateTotalCntAmounts = data[settings.VALIDATE_TOTAL_CNT_AMOUNTS];
        }
    }

    if (data[settings.VIRTUAL_KEYBOARD_ENABLED] !== undefined) {
        if (typeof data[settings.VIRTUAL_KEYBOARD_ENABLED] === "boolean") {
            settingState.virtualKeyboardEnabled = data[settings.VIRTUAL_KEYBOARD_ENABLED];
        }
    }

    if (data[settings.SOUND_ENABLED] !== undefined) {
        if (typeof data[settings.SOUND_ENABLED] === "boolean") {
            settingState.soundEnabled = data[settings.SOUND_ENABLED];
        }
    }

    if (data[settings.SPECTRO_ENABLED] !== undefined) {
        if (typeof data[settings.SPECTRO_ENABLED] === "boolean") {
            settingState.spectroEnabled = data[settings.SPECTRO_ENABLED];
        }
    }

    if (data[settings.STYLE] !== undefined) {
        if (typeof data[settings.STYLE] === "string") {
            settingState.style = data[settings.STYLE];
        }
    }

    if (data[settings.TABLE_FORMULA_NAME] !== undefined) {
        if (typeof data[settings.TABLE_FORMULA_NAME] === "boolean") {
            settingState.tableFormulaName = data[settings.TABLE_FORMULA_NAME];
        }
    }

    if (data[settings.TABLE_FORMULA_CODE] !== undefined) {
        if (typeof data[settings.TABLE_FORMULA_CODE] === "boolean") {
            settingState.tableFormulaCode = data[settings.TABLE_FORMULA_CODE];
        }
    }

    if (data[settings.TABLE_FANDECK] !== undefined) {
        if (typeof data[settings.TABLE_FANDECK] === "boolean") {
            settingState.tableFandeck = data[settings.TABLE_FANDECK];
        }
    }

    if (data[settings.TABLE_PRODUCT] !== undefined) {
        if (typeof data[settings.TABLE_PRODUCT] === "boolean") {
            settingState.tableProduct = data[settings.TABLE_PRODUCT];
        }
    }

    if (data[settings.TABLE_BASE] !== undefined) {
        if (typeof data[settings.TABLE_BASE] === "boolean") {
            settingState.tableBase = data[settings.TABLE_BASE];
        }
    }

    if (data[settings.TABLE_BIPP] !== undefined) {
        if (typeof data[settings.TABLE_BIPP] === "boolean") {
            settingState.tableBipp = data[settings.TABLE_BIPP];
        }
    }

    if (data[settings.TABLE_BUILDING_SITE] !== undefined) {
        if (typeof data[settings.TABLE_BUILDING_SITE] === "boolean") {
            settingState.tableBuildingSite = data[settings.TABLE_BUILDING_SITE];
        }
    }

    if (data[settings.TABLE_OPERATOR] !== undefined) {
        if (typeof data[settings.TABLE_OPERATOR] === "boolean") {
            settingState.tableOperator = data[settings.TABLE_OPERATOR];
        }
    }

    if (data[settings.TABLE_PACKAGE] !== undefined) {
        if (typeof data[settings.TABLE_PACKAGE] === "boolean") {
            settingState.tablePackage = data[settings.TABLE_PACKAGE];
        }
    }

    if (data[settings.TABLE_QUALITY_CONTROL] !== undefined) {
        if (typeof data[settings.TABLE_QUALITY_CONTROL] === "boolean") {
            settingState.tableQualityControl = data[settings.TABLE_QUALITY_CONTROL];
        }
    }

    if (data[settings.TABLE_QUANTITY] !== undefined) {
        if (typeof data[settings.TABLE_QUANTITY] === "boolean") {
            settingState.tableQuantity = data[settings.TABLE_QUANTITY];
        }
    }

    if (data[settings.TABLE_CUSTOMER] !== undefined) {
        if (typeof data[settings.TABLE_CUSTOMER] === "boolean") {
            settingState.tableCustomer = data[settings.TABLE_CUSTOMER];
        }
    }

    if (data[settings.TABLE_MORE_INFO] !== undefined) {
        if (typeof data[settings.TABLE_MORE_INFO] === "boolean") {
            settingState.tableMoreInfo = data[settings.TABLE_MORE_INFO];
        }
    }

    if (data[settings.TABLE_MORE_INFO_INTERNAL] !== undefined) {
        if (typeof data[settings.TABLE_MORE_INFO_INTERNAL] === "boolean") {
            settingState.tableMoreInfoInternal = data[settings.TABLE_MORE_INFO_INTERNAL];
        }
    }

    if (data[settings.TABLE_DATE] !== undefined) {
        if (typeof data[settings.TABLE_DATE] === "boolean") {
            settingState.tableDate = data[settings.TABLE_DATE];
        }
    }

    if (data[settings.TABS_ENABLED] !== undefined) {
        if (typeof data[settings.TABS_ENABLED] === "boolean") {
            settingState.tabsEnabled = data[settings.TABS_ENABLED];
        }
    }

    if (data[settings.TABS_MEMORY] !== undefined) {
        if (typeof data[settings.TABS_MEMORY] === "boolean") {
            settingState.tabsMemory = data[settings.TABS_MEMORY];
        }
    }

    if (data[settings.TINTOMETRIC_SYSTEM] !== undefined) {
        if (typeof data[settings.TINTOMETRIC_SYSTEM] === "string") {
            settingState.tintometricSystem = data[settings.TINTOMETRIC_SYSTEM];
        }
    }

    if (data[settings.TINTOMETRIC_SYSTEMS_ENABLED] !== undefined) {
        if (typeof data[settings.TINTOMETRIC_SYSTEMS_ENABLED] === "boolean") {
            settingState.tintometricSystemsEnabled = data[settings.TINTOMETRIC_SYSTEMS_ENABLED];
        }
    }

    if (data[settings.UFI_CODE_BASE_VALIDATION] !== undefined) {
        if (typeof data[settings.UFI_CODE_BASE_VALIDATION] === "number") {
            settingState.ufiCodeBaseValidation = data[settings.UFI_CODE_BASE_VALIDATION];
        }
    }

    if (data[settings.UFI_CODE_LOGIC] !== undefined) {
        if (typeof data[settings.UFI_CODE_LOGIC] === "number") {
            settingState.ufiCodeLogic = data[settings.UFI_CODE_LOGIC];
        }
    }

    if (data[settings.UFI_CODE_MIN_GRAV_PERCENT] !== undefined) {
        if (typeof data[settings.UFI_CODE_MIN_GRAV_PERCENT] === "number") {
            settingState.ufiCodeMinGravPercent = data[settings.UFI_CODE_MIN_GRAV_PERCENT];
        }
    }

    if (data[settings.UFI_CODE_MIN_AMOUNT_GRAV_PERCENT] !== undefined) {
        if (typeof data[settings.UFI_CODE_MIN_AMOUNT_GRAV_PERCENT] === "number") {
            settingState.ufiCodeMinAmountGravPercent = data[settings.UFI_CODE_MIN_AMOUNT_GRAV_PERCENT];
        }
    }

    if (data[settings.USER_IDENTIFICATION] !== undefined) {
        if (typeof data[settings.USER_IDENTIFICATION] === "boolean") {
            settingState.userIdentificationEnabled = data[settings.USER_IDENTIFICATION];
        }
    }

    if (data[settings.VISIBLE_UNITS] !== undefined) {
        if (typeof data[settings.VISIBLE_UNITS] === "object") {
            settingState.visibleUnits = data[settings.VISIBLE_UNITS];
        }
    }

    if (data[settings.ZOOM] !== undefined) {
        if (typeof data[settings.ZOOM] === "number") {
            settingState.zoom = isNaN(parseInt(data[settings.ZOOM], 10))
                ? settings.DEFAULT_ZOOM
                : parseInt(data[settings.ZOOM], 10);
        }
    }

    if (data[settings.RECIRCULATION_SPEED] !== undefined) {
        if (
            typeof data[settings.RECIRCULATION_SPEED] === "number" &&
            data[settings.RECIRCULATION_SPEED] >= 0 &&
            data[settings.RECIRCULATION_SPEED] <= 100
        ) {
            settingState.recirculationSpeed = data[settings.RECIRCULATION_SPEED];
        }
    }

    if (data[settings.RECIRCULATION_TIME] !== undefined) {
        if (typeof data[settings.RECIRCULATION_TIME] === "number") {
            settingState.recirculationTime = data[settings.RECIRCULATION_TIME];
        }
    }

    if (data[settings.STIRRING_SPEED] !== undefined) {
        if (
            typeof data[settings.STIRRING_SPEED] === "number" &&
            data[settings.STIRRING_SPEED] >= 0 &&
            data[settings.STIRRING_SPEED] <= 100
        ) {
            settingState.stirringSpeed = data[settings.STIRRING_SPEED];
        }
    }

    if (data[settings.STIRRING_TIME] !== undefined) {
        if (typeof data[settings.STIRRING_TIME] === "number") {
            settingState.stirringTime = data[settings.STIRRING_TIME];
        }
    }

    if (data[settings.STOCK_DATABASE_FILTER] !== undefined) {
        if (typeof data[settings.STOCK_DATABASE_FILTER] === "string") {
            settingState.stockDatabaseFilter = data[settings.STOCK_DATABASE_FILTER];
        }
    }

    if (data[settings.STOCK_TABLE_ORDER_BY] !== undefined) {
        if (typeof data[settings.STOCK_TABLE_ORDER_BY] === "number") {
            settingState.stockTableOrderBy = data[settings.STOCK_TABLE_ORDER_BY];
        }
    }

    if (data[settings.CLEANING_TIME] !== undefined) {
        if (typeof data[settings.CLEANING_TIME] === "number") {
            settingState.cleaningTime = data[settings.CLEANING_TIME];
        }
    }

    if (data[settings.INTERGAMMA_COLOR_FANDECK_WIZARD] !== undefined) {
        if (typeof data[settings.INTERGAMMA_COLOR_FANDECK_WIZARD] === "boolean") {
            settingState.intergammaColorFandeckWizard = data[settings.INTERGAMMA_COLOR_FANDECK_WIZARD];
        }
    }

    if (data[settings.INTERGAMMA_MACHINE_SORTING_ORDERS] !== undefined) {
        if (typeof data[settings.INTERGAMMA_MACHINE_SORTING_ORDERS] === "string") {
            settingState.intergammaMachineSortingOrders = data[settings.INTERGAMMA_MACHINE_SORTING_ORDERS];
        }
    }

    if (
        data[settings.WEBER_CAN_BIG_LOWER_BOUND] !== undefined &&
        typeof data[settings.WEBER_CAN_BIG_LOWER_BOUND] === "number"
    ) {
        settingState.weberCanBigLowerBound = data[settings.WEBER_CAN_BIG_LOWER_BOUND];
    } else {
        settingState.weberCanBigLowerBound = settings.WEBER_DEFAULT_CAN_BIG_LOWER_BOUND;
    }

    if (
        data[settings.WEBER_CAN_BIG_UPPER_BOUND] !== undefined &&
        typeof data[settings.WEBER_CAN_BIG_UPPER_BOUND] === "number"
    ) {
        settingState.weberCanBigUpperBound = data[settings.WEBER_CAN_BIG_UPPER_BOUND];
    } else {
        settingState.weberCanBigUpperBound = settings.WEBER_DEFAULT_CAN_BIG_UPPER_BOUND;
    }

    if (
        data[settings.WEBER_CAN_COATING_LOWER_BOUND] !== undefined &&
        typeof data[settings.WEBER_CAN_COATING_LOWER_BOUND] === "number"
    ) {
        settingState.weberCanCoatingLowerBound = data[settings.WEBER_CAN_COATING_LOWER_BOUND];
    } else {
        settingState.weberCanCoatingLowerBound = settings.WEBER_DEFAULT_CAN_COATING_LOWER_BOUND;
    }

    if (
        data[settings.WEBER_CAN_COATING_UPPER_BOUND] !== undefined &&
        typeof data[settings.WEBER_CAN_COATING_UPPER_BOUND] === "number"
    ) {
        settingState.weberCanCoatingUpperBound = data[settings.WEBER_CAN_COATING_UPPER_BOUND];
    } else {
        settingState.weberCanCoatingUpperBound = settings.WEBER_DEFAULT_CAN_COATING_UPPER_BOUND;
    }

    if (
        data[settings.WEBER_CAN_LABORATORY_LOWER_BOUND] !== undefined &&
        typeof data[settings.WEBER_CAN_LABORATORY_LOWER_BOUND] === "number"
    ) {
        settingState.weberCanLaboratoryLowerBound = data[settings.WEBER_CAN_LABORATORY_LOWER_BOUND];
    } else {
        settingState.weberCanLaboratoryLowerBound = settings.WEBER_DEFAULT_CAN_LABORATORY_LOWER_BOUND;
    }

    if (
        data[settings.WEBER_CAN_LABORATORY_UPPER_BOUND] !== undefined &&
        typeof data[settings.WEBER_CAN_LABORATORY_UPPER_BOUND] === "number"
    ) {
        settingState.weberCanLaboratoryUpperBound = data[settings.WEBER_CAN_LABORATORY_UPPER_BOUND];
    } else {
        settingState.weberCanLaboratoryUpperBound = settings.WEBER_DEFAULT_CAN_LABORATORY_UPPER_BOUND;
    }

    if (
        data[settings.WEBER_CAN_SMALL_LOWER_BOUND] !== undefined &&
        typeof data[settings.WEBER_CAN_SMALL_LOWER_BOUND] === "number"
    ) {
        settingState.weberCanSmallLowerBound = data[settings.WEBER_CAN_SMALL_LOWER_BOUND];
    } else {
        settingState.weberCanSmallLowerBound = settings.WEBER_DEFAULT_CAN_SMALL_LOWER_BOUND;
    }

    if (
        data[settings.WEBER_CAN_SMALL_UPPER_BOUND] !== undefined &&
        typeof data[settings.WEBER_CAN_SMALL_UPPER_BOUND] === "number"
    ) {
        settingState.weberCanSmallUpperBound = data[settings.WEBER_CAN_SMALL_UPPER_BOUND];
    } else {
        settingState.weberCanSmallUpperBound = settings.WEBER_DEFAULT_CAN_SMALL_UPPER_BOUND;
    }

    if (data[settings.WEBER_COATING_NAMES] !== undefined && Array.isArray(data[settings.WEBER_COATING_NAMES])) {
        let c,
            coatings = [];
        for (c of data[settings.WEBER_COATING_NAMES]) {
            if (typeof c === "string") {
                coatings.push(c);
            }
        }
        settingState.weberCoatingNames = coatings;
    } else {
        settingState.weberCoatingNames = settings.WEBER_DEFAULT_COATING_NAMES;
    }

    if (data[settings.WEBER_PLASTER_NAMES] !== undefined && Array.isArray(data[settings.WEBER_PLASTER_NAMES])) {
        let p,
            plasters = [];
        for (p of data[settings.WEBER_PLASTER_NAMES]) {
            if (typeof p === "string") {
                plasters.push(p);
            }
        }
        settingState.weberPlasterNames = plasters;
    } else {
        settingState.weberPlasterNames = settings.WEBER_DEFAULT_PLASTER_NAMES;
    }

    if (redlikeLightSoftware) {
        settingState.tabsEnabled = false;
    }

    if (gammaSoftware) {
        settingState.tabsEnabled = false;
        settingState.searchPanelProduct = true;
        settingState.searchPanelBipp = true;
    }

    return settingState;
};

export const checkLastUsedUnitVisibility = (lastUsedUnit, visibleUnits, formulaUnit) => {
    if (lastUsedUnit === settings.UNIT_ML && !visibleUnits.ml) {
        return formulaUnit;
    }
    if (lastUsedUnit === settings.UNIT_G && !visibleUnits.g) {
        return formulaUnit;
    }
    if (lastUsedUnit === settings.UNIT_SHOT && !visibleUnits.shots) {
        return formulaUnit;
    }
    return lastUsedUnit;
};

export const parsePricingSettings = (data) => {
    let settingState = {
        showLocalPrices: false,
        showMargin: true,
        showMarkup: false,
        showDiscount: false,
        priceGroupsEnabled: false,
        priceGroupNumber: 0,
        priceDefinition: settings.PURCHASE_ONLY,
        priceUnitDefinition: 1
    };

    if (data[settings.PRICING_LOCAL_PRICES] !== undefined) {
        if (typeof data[settings.PRICING_LOCAL_PRICES] === "boolean") {
            settingState.showLocalPrices = data[settings.PRICING_LOCAL_PRICES];
        }
    }

    if (data[settings.PRICING_USE_MARGIN] !== undefined) {
        if (typeof data[settings.PRICING_USE_MARGIN] === "boolean") {
            settingState.showMargin = data[settings.PRICING_USE_MARGIN];
        }
    }

    if (data[settings.PRICING_USE_MARKUP] !== undefined) {
        if (typeof data[settings.PRICING_USE_MARKUP] === "boolean") {
            settingState.showMarkup = data[settings.PRICING_USE_MARKUP];
        }
    }

    if (data[settings.PRICING_USE_DISCOUNT] !== undefined) {
        if (typeof data[settings.PRICING_USE_DISCOUNT] === "boolean") {
            settingState.showDiscount = data[settings.PRICING_USE_DISCOUNT];
        }
    }

    if (data[settings.PRICING_PRICE_DEFINITION] !== undefined) {
        if (typeof data[settings.PRICING_PRICE_DEFINITION] === "string") {
            settingState.priceDefinition = data[settings.PRICING_PRICE_DEFINITION];
        }
    }

    if (data[settings.PRICING_PRICE_UNIT_DEFINITION] !== undefined) {
        if (typeof data[settings.PRICING_PRICE_UNIT_DEFINITION] === "number") {
            settingState.priceUnitDefinition = data[settings.PRICING_PRICE_UNIT_DEFINITION];
        }
    }

    if (data[settings.PRICING_PRICE_GROUPS_ENABLED] !== undefined) {
        if (typeof data[settings.PRICING_PRICE_GROUPS_ENABLED] === "boolean") {
            settingState.priceGroupsEnabled = data[settings.PRICING_PRICE_GROUPS_ENABLED];
        }
    }

    if (data[settings.PRICING_NUMBER_PRICE_GROUPS] !== undefined) {
        if (typeof data[settings.PRICING_NUMBER_PRICE_GROUPS] === "number") {
            settingState.priceGroupNumber = data[settings.PRICING_NUMBER_PRICE_GROUPS];
        }
    }

    return settingState;
};

export const parseSettingOptions = (data, productPricing) => {
    let options = {
        additionalTabTypes: [],
        alternativeFormulasPosition: [],
        colorPreviewResizable: [],
        colorTabRowCount: [],
        colorTabOrderBy: [],
        colorantPanelOrderBy: [],
        customBannerPosition: [],
        customCanOption: [],
        formulaInfoData: [],
        formulaPreview: [],
        formulaPropertiesPosition: [],
        historicalFormulasPosition: [],
        imagesSource: [],
        infoPanelResizable: [],
        initTabType: [],
        keyboard: [],
        keyboardShortcuts: [],
        navigationItems: [],
        orderSearchItems: [],
        priceTypes: [],
        priceVisibilityTypes: [],
        productFilesSource: [],
        qualityControl: [settings.CIE_LAB_2000, settings.CIE_LAB_1976, settings.CMC],
        quickTintSortingType: [],
        ufiCodeBaseValidation: [],
        ufiCodeLogic: [],
        zoom: []
    };

    if (data[settings.ADDITIONAL_TAB_TYPE] !== undefined) {
        options.additionalTabTypes = data[settings.ADDITIONAL_TAB_TYPE];
    }

    if (data[settings.ALTERNATIVE_FORMULAS_POSITION] !== undefined) {
        options.alternativeFormulasPosition = data[settings.ALTERNATIVE_FORMULAS_POSITION];
    }

    if (data[settings.COLOR_PREVIEW_RESIZABLE] !== undefined) {
        options.colorPreviewResizable = data[settings.COLOR_PREVIEW_RESIZABLE];
    }

    if (data[settings.COLOR_TAB_ROW_COUNT] !== undefined) {
        options.colorTabRowCount = data[settings.COLOR_TAB_ROW_COUNT];
    }

    if (data[settings.COLOR_TAB_ORDER_BY] !== undefined) {
        options.colorTabOrderBy = data[settings.COLOR_TAB_ORDER_BY];
    }

    if (data[settings.COLORANT_PANEL_ORDER_BY] !== undefined) {
        options.colorantPanelOrderBy = data[settings.COLORANT_PANEL_ORDER_BY];
    }

    if (data[settings.CUSTOM_BANNER_POSITION] !== undefined) {
        options.customBannerPosition = data[settings.CUSTOM_BANNER_POSITION];
    }

    if (data[settings.CUSTOM_CAN_OPTION] !== undefined) {
        options.customCanOption = data[settings.CUSTOM_CAN_OPTION];
    }

    if (data[settings.FORMULA_INFO_DATA] !== undefined) {
        options.formulaInfoData = data[settings.FORMULA_INFO_DATA];
    }

    if (data[settings.FORMULA_PREVIEW] !== undefined) {
        options.formulaPreview = data[settings.FORMULA_PREVIEW];
    }

    if (data[settings.FORMULA_PROPERTIES_POSITION] !== undefined) {
        options.formulaPropertiesPosition = data[settings.FORMULA_PROPERTIES_POSITION];
    }

    if (data[settings.HISTORICAL_FORMULAS_POSITION] !== undefined) {
        options.historicalFormulasPosition = data[settings.HISTORICAL_FORMULAS_POSITION];
    }

    if (data[settings.IMAGES_SOURCE] !== undefined) {
        options.imagesSource = data[settings.IMAGES_SOURCE];
    }

    if (data[settings.INFO_PANEL_RESIZABLE] !== undefined) {
        options.infoPanelResizable = data[settings.INFO_PANEL_RESIZABLE];
    }

    if (data[settings.INIT_TAB_TYPE] !== undefined) {
        options.initTabType = data[settings.INIT_TAB_TYPE];
    }

    if (data[settings.KEYBOARD] !== undefined) {
        options.keyboard = data[settings.KEYBOARD];
    }

    if (data[settings.KEYBOARD_SHORTCUTS] !== undefined) {
        options.keyboardShortcuts = getShortcutsByLicense(data[settings.KEYBOARD_SHORTCUTS], productPricing);
    }

    if (data[settings.NAVIGATION_ITEMS_OPTIONS] !== undefined) {
        options.navigationItems = getNavigationItemsByLicense(data[settings.NAVIGATION_ITEMS_OPTIONS], productPricing);
    }

    if (data[settings.ORDER_SEARCH_ITEMS] !== undefined) {
        options.orderSearchItems = data[settings.ORDER_SEARCH_ITEMS];
    }

    if (data[settings.PRICE_TYPES] !== undefined) {
        options.priceTypes = data[settings.PRICE_TYPES];
    }

    if (data[settings.PRICE_VISIBILITY_TYPES] !== undefined) {
        options.priceVisibilityTypes = data[settings.PRICE_VISIBILITY_TYPES];
    }

    if (data[settings.PRODUCT_FILES_SOURCE] !== undefined) {
        options.productFilesSource = data[settings.PRODUCT_FILES_SOURCE];
    }

    if (data[settings.QUICK_TINT_SORTING_TYPE] !== undefined) {
        options.quickTintSortingType = data[settings.QUICK_TINT_SORTING_TYPE];
    }

    if (data[settings.UFI_CODE_BASE_VALIDATION] !== undefined) {
        options.ufiCodeBaseValidation = data[settings.UFI_CODE_BASE_VALIDATION];
    }

    if (data[settings.UFI_CODE_LOGIC] !== undefined) {
        options.ufiCodeLogic = data[settings.UFI_CODE_LOGIC];
    }

    if (data[settings.ZOOM] !== undefined) {
        options.zoom = data[settings.ZOOM];
    }

    return options;
};

export const parseHelpItems = (data) => {
    let helpItems = {telephone: config.SUPPORT_NUMBER, email: config.SUPPORT_EMAIL, message: ""};

    if (data === undefined || data === null || typeof data !== "object") {
        return helpItems;
    }

    if (data["telephone"] !== undefined) {
        helpItems.telephone = data["telephone"];
    }
    if (data["message"] !== undefined) {
        helpItems.message = data["message"];
    }
    if (data["email"] !== undefined) {
        helpItems.email = data["email"];
    }

    return helpItems;
};
